import { defineStore } from 'pinia'
import NotificationService from '@/services/header/NotificationService'

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    loadingCount: false,
    loadingList: false,
    notificationCount: 0,
    notificationList: [],
    page: 1,
    maxPages: null,
    limit: 4,
    loadingMore: false,
    listRequestId: 0,
    countRequestId: 0
  }),
  actions: {
    async fetchNotificationCount() {
      const requestId = ++this.countRequestId
      this.loadingCount = true
      try {
        const response = await NotificationService.getNotificationCount()
        if (requestId !== this.countRequestId) return null
        this.notificationCount = response.count
        return response
      } finally {
        if (requestId === this.countRequestId) this.loadingCount = false
      }
    },
    async refreshNotificationCountSafely() {
      try {
        await this.fetchNotificationCount()
      } catch {
        // Count refresh must not rollback the notification action that already succeeded.
      }
    },
    async refreshNotificationList() {
      const requestId = ++this.listRequestId
      this.page = 1
      this.maxPages = null
      this.notificationList = []
      this.loadingList = true
      this.loadingMore = false

      try {
        const response = await NotificationService.getNotificationList(1, this.limit)
        if (requestId !== this.listRequestId) return null

        this.notificationList = response.data
        this.maxPages = response.max_page
        this.page = response.page
        return response
      } finally {
        if (requestId === this.listRequestId) this.loadingList = false
      }
    },
    async loadMoreNotification() {
      if (this.loadingList || this.loadingMore || this.page >= this.maxPages) return null

      const requestId = this.listRequestId
      const requestedPage = this.page + 1
      this.loadingMore = true

      try {
        const response = await NotificationService.getNotificationList(requestedPage, this.limit)
        if (requestId !== this.listRequestId || response.page !== requestedPage) return null

        const existingIds = new Set(this.notificationList.map(({ technical_id }) => technical_id))
        this.notificationList.push(
          ...response.data.filter(({ technical_id }) => !existingIds.has(technical_id))
        )
        this.maxPages = response.max_page
        this.page = requestedPage
        return response
      } finally {
        if (requestId === this.listRequestId) this.loadingMore = false
      }
    },
    async markNotificationOpen(notificationTechnicalId) {
      const response = await NotificationService.updateNotificationToOpen(notificationTechnicalId)
      const notification = this.notificationList.find(
        (item) => item.technical_id === notificationTechnicalId
      )
      if (notification) {
        notification.open = response.open
      }
      await this.refreshNotificationCountSafely()
      return response
    },
    async hideNotification(notificationTechnicalId) {
      const response = await NotificationService.updateNotificationToHidden(notificationTechnicalId)
      if (response.technical_id === notificationTechnicalId) {
        await this.refreshNotificationList()
        await this.refreshNotificationCountSafely()
      }
      return response
    },
    async markRepairFileNotificationOpen(repairFileBoomerangId) {
      await NotificationService.updateRepairFileNotificationToOpen(repairFileBoomerangId)

      const notification = this.notificationList.find((item) => {
        if (typeof item.redirection_url !== 'string') return false

        const match = item.redirection_url.match(
          /^\/(?:repair-file\/detail|repair-file-detail)\/([^/?#]+)/
        )
        return match?.[1] === repairFileBoomerangId && item.open === false
      })
      if (notification) {
        notification.open = true
      }
      await this.refreshNotificationCountSafely()
    }
  }
})
